import { useEffect } from 'react';

import { useUserCause } from 'core/Api';
import { OnboardingLayout } from 'core/Components';
import { useBreakPoints } from 'core/Hooks';
import { useSetAtom } from 'jotai';
import { AppShell, Card } from 'ui';

import { StoreProvider } from '../../Context';
import {
  DonationCheckoutProvider,
  donationCauseIdAtom,
} from '../../Context/donations/donationsCheckout.provider';
import { useShowDonationItem } from '../../api';
import { CauseDescription } from '../../components/Cause/CauseDescription';
import { MobileCauseHeader } from '../../components/Cause/MobileCauseHeader';
import { StoreItemOptions } from '../../components/organisms/modals/StoreItemOptionsModal';
import { PostDonation } from '../components/PostDonation/PostDonation';
import { SimpleDonationFlowProvider, useSimpleDonations } from '../context';

interface SimpleDonationPageProps {
  causeId?: string;
  onLastStepComplete?: () => void;
}

const SimpleDonation = ({ causeId, storeItemId }: { causeId: string; storeItemId: string }) => {
  const { isMediumScreen } = useBreakPoints();

  return (
    <AppShell>
      <OnboardingLayout leftSideContent={<CauseDescription />} withHeaderSpace>
        {isMediumScreen && <MobileCauseHeader />}
        <Card>
          <StoreItemOptions
            causeId={causeId}
            storeItemId={storeItemId}
            isAddToCartAvailable={false}
            checkoutButtonsProps={{ buyNowProps: { buttonProps: { children: 'Donate Now' } } }}
          />
        </Card>
      </OnboardingLayout>
    </AppShell>
  );
};

// A wrapper for the simple donations Page that allows us access to the context
// This component should be able to move us from simple donations to post donations. Not much more
const SimpleDonationWrapper = ({ causeId }: SimpleDonationPageProps) => {
  const { displaySection } = useSimpleDonations();
  const setStoreCauseId = useSetAtom(donationCauseIdAtom);
  const { data: cause } = useUserCause();
  const { data: donationItem } = useShowDonationItem({ storeId: causeId });

  useEffect(() => {
    const donationCause = causeId ?? cause?.uuid ?? '';
    setStoreCauseId(donationCause);
  }, [causeId, cause, setStoreCauseId]);

  if (!causeId || !donationItem?.uuid) return null;
  if (displaySection == 1)
    return <SimpleDonation causeId={causeId} storeItemId={donationItem?.uuid} />;

  return <PostDonation />;
};

// It looks like a lot of providers, but really we could separate all of these out.
const SimpleDonationPage = ({ onLastStepComplete, causeId }: SimpleDonationPageProps) => {
  return (
    <StoreProvider>
      <DonationCheckoutProvider>
        <SimpleDonationFlowProvider onLastStepComplete={onLastStepComplete}>
          <SimpleDonationWrapper causeId={causeId} />
        </SimpleDonationFlowProvider>
      </DonationCheckoutProvider>
    </StoreProvider>
  );
};

export { SimpleDonationPage };
